import { useCallback } from 'react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import type { RootState } from '@/store'
import { upsertReminder, removeReminder, setReminders } from '@/store/slices/remindersSlice'
import { remindersApi } from '@/api/remindersApi'
import { publishReminderEvent } from './reminderBroadcast'
import { clearSurfaced } from './surfacedLedger'
import { normalizeReminder } from './reminderTime'
import type { Reminder, ReminderDraft } from './types'
import type { SnowflakeId } from '@/types/snowflake'

/** `_tz` 暂时没有 action 用到(snooze 的时区换算下沉到了 SnoozeMenu),
 *  保留入参让 4 个调用点(Bell / Panel / Host / AddReminderPopover)签名不变。 */
export function useReminderActions(_tz: string) {
  const { t } = useTranslation('reminders')
  const dispatch = useDispatch()
  const items = useSelector((s: RootState) => s.reminders.items)

  const add = useCallback(async (draft: ReminderDraft) => {
    try {
      const created = normalizeReminder(await remindersApi.create(draft))
      dispatch(upsertReminder(created))
      publishReminderEvent({ kind: 'upserted', reminder: created })
    } catch {
      toast.error(t('toastCreateFailed'))
    }
  }, [dispatch, t])

  const patch = useCallback(async (id: SnowflakeId, data: Partial<ReminderDraft> & { status?: string; snoozedFrom?: string }, optimistic: Reminder) => {
    // per-item 回滚:只恢复本条,不用全列表快照——否则快速连续保存时,
    // 前一次失败的回滚会吞掉后一次成功保存的乐观更新(以及其它无关条目的更新)
    const prior = items.find((x) => x.id === id)
    dispatch(upsertReminder(optimistic))
    publishReminderEvent({ kind: 'upserted', reminder: optimistic })
    try {
      await remindersApi.update(id, data)
    } catch {
      if (prior) {
        dispatch(upsertReminder(prior))
        publishReminderEvent({ kind: 'upserted', reminder: prior }) // 乐观更新已广播过,失败也要广播回滚让其它 tab 收敛
      }
      toast.error(t('toastUpdateFailed'))
    }
  }, [items, dispatch, t])

  const toggle = useCallback((id: SnowflakeId) => {
    const r = items.find((x) => x.id === id)
    if (!r) return
    const next: Reminder = { ...r, status: r.status === 'done' ? 'open' : 'done' }
    if (next.status === 'open') clearSurfaced(String(id))
    void patch(id, { status: next.status }, next)
  }, [items, patch])

  /** 挪到指定日期/时间(dueTime 传 null = 全天 Anytime,后端会清空 due_time)。 */
  const snoozeTo = useCallback((id: SnowflakeId, dueDate: string, dueTime: string | null) => {
    const r = items.find((x) => x.id === id)
    if (!r) return
    clearSurfaced(String(id))
    const next: Reminder = { ...r, dueDate, dueTime, status: 'open' }
    void patch(id, { dueDate, dueTime, snoozedFrom: `${r.dueDate}T${r.dueTime ?? '00:00'}` }, next)
  }, [items, patch])

  const updateText = useCallback((id: SnowflakeId, text: string) => {
    const r = items.find((x) => x.id === id)
    if (!r) return
    const next: Reminder = { ...r, text }
    void patch(id, { text }, next)
  }, [items, patch])

  const remove = useCallback(async (id: SnowflakeId) => {
    const prior = items.find((x) => x.id === id)
    dispatch(removeReminder(id))
    publishReminderEvent({ kind: 'removed', id })
    try {
      await remindersApi.remove(id)
      clearSurfaced(String(id))
    } catch {
      // per-item 恢复:只把删除失败的这条放回,不整体回滚快照
      if (prior) {
        dispatch(upsertReminder(prior))
        publishReminderEvent({ kind: 'upserted', reminder: prior })
      }
      toast.error(t('toastDeleteFailed'))
    }
  }, [items, dispatch, t])

  const clearDone = useCallback(async () => {
    const doneItems = items.filter((r) => r.status === 'done')
    if (doneItems.length === 0) return
    dispatch(setReminders(items.filter((r) => r.status !== 'done')))
    const settled = await Promise.allSettled(doneItems.map((r) => remindersApi.remove(r.id)))
    // 按结果精确恢复:只把删除失败的放回,删成功的不能因整体快照回滚而"复活"
    const failedIds = new Set(doneItems.filter((_, i) => settled[i].status === 'rejected').map((r) => r.id))
    if (failedIds.size > 0) {
      dispatch(setReminders(items.filter((r) => r.status !== 'done' || failedIds.has(r.id))))
      toast.error(t('toastDeleteFailed'))
    }
    // 成败与否都广播:其它 tab 需要收敛已部分删除的真实状态
    publishReminderEvent({ kind: 'refetch' })
  }, [items, dispatch, t])

  return { add, toggle, snoozeTo, updateText, remove, clearDone }
}
